/**
 * @sazeo/sdk — Settings hooks
 *
 * TanStack Query wrappers for user self-settings and tenant company settings.
 * All calls use the user JWT. No service_role.
 */

import type {
  AvatarUploadResponse,
  LogoUploadResponse,
  MeResponse,
  TenantSettings,
  TenantSettingsUpdateInput,
  UserProfileUpdate,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const settingsKeys = {
  all: ['settings'] as const,
  me: () => [...settingsKeys.all, 'me'] as const,
  tenant: (tenantId: string) => [...settingsKeys.all, 'tenant', tenantId] as const,
  integrations: (tenantId: string) => [...settingsKeys.all, 'integrations', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * useMeSettings — GET /settings/me
 * Composite: users row + membership role + memberships_profile.
 */
export function useMeSettings(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: settingsKeys.me(),
    queryFn: () => apiFetch<MeResponse>(supabase, 'settings/me'),
  });
}

/**
 * useUpdateMeSettings — PATCH /settings/me
 * Self-edit: theme, preferred_lang, timezone, avatar_url, phone, display_name.
 */
export function useUpdateMeSettings(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserProfileUpdate) =>
      apiFetch<MeResponse>(supabase, 'settings/me', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.me() });
      // Sync with the people module's me cache
      void qc.invalidateQueries({ queryKey: ['people', 'me'] });
    },
  });
}

/**
 * useRequestAvatarUpload — POST /settings/me/avatar
 * Returns a signed upload URL plus the public URL that should be persisted
 * back into `users.avatar_url` after the client PUT completes.
 */
export function useRequestAvatarUpload(supabase: SupabaseClientLike) {
  return useMutation({
    mutationFn: (fileExt: 'png' | 'jpg' | 'webp') =>
      apiFetch<AvatarUploadResponse>(supabase, 'settings/me/avatar', {
        method: 'POST',
        body: JSON.stringify({ ext: fileExt }),
      }),
  });
}

/**
 * useTenantSettings — GET /settings/tenant
 * Any member can read; admin/owner for writes.
 */
export function useTenantSettings(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: settingsKeys.tenant(tenantId),
    queryFn: () => apiFetch<TenantSettings>(supabase, 'settings/tenant'),
    enabled: Boolean(tenantId),
  });
}

/**
 * useUpdateTenantSettings — PATCH /settings/tenant
 * Admin/owner only; enforced server-side.
 */
export function useUpdateTenantSettings(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantSettingsUpdateInput) =>
      apiFetch<TenantSettings>(supabase, 'settings/tenant', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.tenant(tenantId) });
    },
  });
}

/**
 * useRequestLogoUpload — POST /settings/tenant/logo
 * Returns a signed upload URL for the tenant logo (admin/owner only).
 * The caller PUTs the file directly to Supabase Storage via the signed URL;
 * binary data never flows through the Edge Function.
 */
export function useRequestLogoUpload(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileExt: 'png' | 'svg') =>
      apiFetch<LogoUploadResponse>(supabase, 'settings/tenant/logo', {
        method: 'POST',
        body: JSON.stringify({ ext: fileExt }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.tenant(tenantId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Tenant integration settings
// ---------------------------------------------------------------------------

export type TenantIntegrationProvider = 'gmail_oauth' | 'anthropic' | 'cloudflare_dns';
export type TenantIntegrationStatus = 'missing' | 'configured' | 'needs_attention';

export interface TenantIntegrationRecord {
  provider: TenantIntegrationProvider;
  status: TenantIntegrationStatus;
  config: Record<string, unknown>;
  secretFingerprint: string | null;
  updatedAt: string | null;
}

export interface TenantIntegrationsResponse {
  integrations: TenantIntegrationRecord[];
}

export interface UpdateTenantIntegrationInput {
  provider: TenantIntegrationProvider;
  config?: Record<string, unknown>;
  secret?: string;
}

export function useTenantIntegrations(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: settingsKeys.integrations(tenantId),
    queryFn: () => apiFetch<TenantIntegrationsResponse>(supabase, 'settings/tenant/integrations'),
    enabled: Boolean(tenantId),
  });
}

export function useUpdateTenantIntegration(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTenantIntegrationInput) =>
      apiFetch<TenantIntegrationRecord>(
        supabase,
        `settings/tenant/integrations/${encodeURIComponent(input.provider)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            config: input.config ?? {},
            secret: input.secret,
          }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.integrations(tenantId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Data export hooks
// ---------------------------------------------------------------------------

export interface DataExport {
  id: string;
  tenant_id: string | null;
  user_id: string;
  mode: 'tenant_full' | 'user_personal' | 'regulatory';
  scope: Record<string, unknown> | null;
  status: 'queued' | 'generating' | 'ready' | 'expired' | 'failed';
  download_url: string | null;
  file_size_bytes: number | null;
  expires_at: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

/**
 * useDataExports — GET /data-export/
 * Returns the authenticated user's export history.
 * Polls every 10s while any job is in-progress.
 */
export function useDataExports(supabase: SupabaseClientLike, userId: string | undefined) {
  return useQuery({
    queryKey: ['data-exports', userId],
    queryFn: () => apiFetch<DataExport[]>(supabase, 'data-export'),
    enabled: !!userId,
    refetchInterval: (query) => {
      const exports = query.state.data as DataExport[] | undefined;
      const hasActive = exports?.some((e) => e.status === 'queued' || e.status === 'generating');
      return hasActive ? 10_000 : false;
    },
  });
}

/**
 * useRequestDataExport — POST /data-export/request
 * Queues a new export job. Invalidates the data-exports query on success.
 */
export function useRequestDataExport(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ mode }: { mode: 'user_personal' | 'tenant_full' }) => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch(buildFunctionUrl('data-export/request'), {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...headers },
        body: JSON.stringify({ mode }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? res.statusText);
      }
      return res.json() as Promise<{ id: string; status: string }>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['data-exports'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Tenant deletion hooks
// ---------------------------------------------------------------------------

export interface TenantDeletionStatus {
  scheduled_at: string;
  days_remaining: number;
  reason: string | null;
}

/**
 * useTenantDeletion — composite hook for tenant soft-delete lifecycle.
 *
 * Returns:
 *   status          — current deletion status (null = not scheduled)
 *   scheduleDeletion — owner only: POST /tenant-deletion/schedule
 *   cancelDeletion   — owner only: DELETE /tenant-deletion/cancel
 */
export function useTenantDeletion(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  const queryKey = ['tenant-deletion', 'status', tenantId] as const;

  const status = useQuery({
    queryKey,
    queryFn: () => apiFetch<TenantDeletionStatus | null>(supabase, 'tenant-deletion/status'),
    enabled: Boolean(tenantId),
  });

  const scheduleDeletion = useMutation({
    mutationFn: ({ reason }: { reason?: string } = {}) =>
      apiFetch<TenantDeletionStatus>(supabase, 'tenant-deletion/schedule', {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const cancelDeletion = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch(buildFunctionUrl('tenant-deletion/cancel'), {
        method: 'DELETE',
        headers: { 'content-type': 'application/json', ...headers },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? res.statusText);
      }
      // 204 No Content — no body to parse.
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  return { status, scheduleDeletion, cancelDeletion };
}

/**
 * useCompleteOnboarding — PATCH /settings/tenant { onboarding_completed_at }
 * Called on the final step of the onboarding wizard.
 * Stores the completion timestamp so the wizard is not shown again.
 */
export function useCompleteOnboarding(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<TenantSettings>(supabase, 'settings/tenant', {
        method: 'PATCH',
        body: JSON.stringify({ onboarding_completed_at: new Date().toISOString() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.tenant(tenantId) });
    },
  });
}
